// src/features/chat/models/artifactLifecycleModel.js
/*
 * Lifecycle display states for Zero-effort artifacts.
 *
 * Pure derivations from artifact docs (and callable results) to the display
 * phases of the materialize → commit → settle lifecycle. Status vocabulary
 * never reaches the user from here; user-facing text lives in the template
 * library (src/features/chat/templates/).
 */

export const TERMINAL_APPROVAL_STATUSES = new Set([
  'approved',
  'rejected',
  'unavailable',
  'failed',
  'expired',
]);

/**
 * The user-safe reason code behind an approval's current status.
 * @param {Object} approval
 * @returns {string|null}
 */
export function approvalReasonCode(approval) {
  if (!approval) return null;
  if (approval.status === 'unavailable') {
    return approval.unavailableReason || approval.reason || null;
  }
  if (approval.status === 'failed') {
    return approval.failureReason || approval.reason || null;
  }
  if (approval.status === 'expired') {
    return approval.reason || 'approval_expired';
  }
  if (approval.status === 'rejected') {
    return approval.reason || 'rejected_by_user';
  }
  return approval.reason || approval.unavailableReason || approval.failureReason || null;
}

/**
 * Display phase of an approval artifact.
 * - 'pending': the commit card, waiting in place.
 * - 'resolved_confirmed' / 'resolved_declined': settled whisper.
 * - 'trace': terminal conflict/failure/expiry trace (never folds).
 * @param {Object} approval
 * @returns {'pending'|'resolved_confirmed'|'resolved_declined'|'trace'}
 */
export function approvalDisplayState(approval) {
  const status = approval?.status || 'pending';
  if (status === 'approved') return 'resolved_confirmed';
  if (status === 'rejected') return 'resolved_declined';
  if (TERMINAL_APPROVAL_STATUSES.has(status)) return 'trace';
  return 'pending';
}

/**
 * Display phase of a receipt artifact.
 * @param {Object} receipt
 * @returns {'committed'|'undone'}
 */
export function receiptDisplayState(receipt) {
  if (receipt?.status === 'undone' || receipt?.undo?.status === 'consumed') {
    return 'undone';
  }
  return 'committed';
}

/**
 * Whether the receipt's act can still be undone (live server truth).
 * @param {Object} receipt
 * @returns {boolean}
 */
export function canUndoReceipt(receipt) {
  return (
    receipt?.status === 'committed' &&
    receipt?.undo?.supported !== false &&
    receipt?.undo?.status === 'available'
  );
}

/**
 * Normalize a terminal callable result into an approval-doc-shaped overlay,
 * so resolution renders immediately while Firestore catches up.
 * @param {Object} result - Callable result.
 * @returns {Object|null}
 */
export function terminalApprovalResult(result) {
  const status = result?.status;
  if (!TERMINAL_APPROVAL_STATUSES.has(status)) return null;
  const reason = result.reason ||
    result.unavailableReason ||
    result.failureReason ||
    result.warningReason ||
    null;
  return {
    status,
    reason,
    unavailableReason: status === 'unavailable' ? reason : undefined,
    failureReason: status === 'failed' ? reason : undefined,
    warningReason: reason === 'side_effect_warning' ? reason : undefined,
    result: reason === 'side_effect_warning'
      ? { warningReason: 'side_effect_warning' }
      : undefined,
  };
}

/**
 * Apply a terminal overlay to a still-pending approval doc.
 * @param {Object} approval
 * @param {Object|null} terminalResult
 * @returns {Object}
 */
export function withTerminalOverlay(approval, terminalResult) {
  if (!terminalResult || (approval.status || 'pending') !== 'pending') return approval;
  return {
    ...approval,
    ...terminalResult,
  };
}
